/**
 * Onboarding — bringing a new tenant up, and refusing to pretend it is ready.
 *
 * Three things this screen does that a settings page would not:
 *
 *  1. **Every step says what it VERIFIED, not what was saved.** "Connect Meta" is not a form that
 *     stores an app id; it is a call to Meta that comes back with the token's scopes and its
 *     expiry. A step that could not run says NOT RUN with the reason, in the same size type as a
 *     pass, because a check that did not happen is the one thing that must never look like one.
 *  2. **The ladder shows its refusals.** Every gate is listed whether it is satisfied or not, and
 *     an unmet one shows what would satisfy it. A disabled button with no explanation is how a
 *     person ends up editing the database by hand.
 *  3. **It never claims the tenant has moved when only the database has.** The runtime reads the
 *     PACK, so when the two disagree the screen says exactly which line of `tenant.yaml` is still
 *     owed.
 *
 * The Meta access token has no field on this screen and never will: it lives in the process
 * environment, the API reads it there, and what comes back is Meta's answer about it.
 *
 * Author: Danish Khan
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState, type ReactNode } from 'react';
import { Button } from '../components/Button.tsx';
import { Pill } from '../components/Pill.tsx';
import { ErrorState } from '../components/ErrorState.tsx';
import { formatDateTime } from '../lib/format.ts';
import { canMoveTenantStatus, canOnboard, roleGate } from '../lib/pii.ts';
import { useUser } from '../lib/session.tsx';
import {
  onboardingApi, stateLabel, stateTone,
  type Gate, type LadderStatus, type MoveResult, type OnboardingClient, type Step, type StepId,
} from './onboardingApi.ts';

const INPUT = 'h-9 rounded-[var(--h1-radius)] border border-line-strong bg-bg px-3 text-sm text-fg disabled:opacity-50';

export interface OnboardingProps {
  /** Injected in tests. The page is otherwise pointed at `/api/v1/onboarding`. */
  client?: OnboardingClient;
}

export function OnboardingPage({ client = onboardingApi }: OnboardingProps = {}): ReactNode {
  const qc = useQueryClient();
  const user = useUser();
  /**
   * The two gates this screen carries, and why neither is a rank comparison.
   *
   * Running a step or confirming something with the client is `anyRole: [owner, ops]` at the API,
   * and that SET is the point: `ops` sits at the bottom of the role order precisely so an operator
   * never inherits the client's customer phone numbers, and `ops` is nevertheless who brings a
   * tenant up. `manager` and `rep` rank above `ops` and may not run a step at all, so "manager or
   * above" would be exactly backwards here. Moving the tenant's status is narrower still: owner
   * only, not even the operator who satisfied every gate.
   *
   * Every role keeps the whole screen to read. An operator who cannot see why a tenant is not live
   * cannot diagnose it, and a manager who cannot see the gates cannot tell the owner what is owed.
   */
  const mayRun = canOnboard(user.role);
  const runGate = roleGate(
    mayRun,
    'Running a step and confirming things with the client are for the owner or an operator',
    user.role,
  );
  const mayMove = canMoveTenantStatus(user.role);
  const moveGate = roleGate(
    mayMove,
    'Moving this tenant between development, shadow, live and paused is owner-only',
    user.role,
  );
  const state = useQuery({ queryKey: ['onboarding'], queryFn: () => client.state() });
  const [failed, setFailed] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<StepId | null>(null);
  /**
   * What the last status move did to the RUNNING process.
   *
   * The API has returned `inForce`/`inForceDetail` on every move since the pack swap was written,
   * and this screen dropped both on the floor: it refetched, saw the new status in the column, and
   * showed a pill. That pill is the thing the operator reads after pressing **Move to paused**
   * while an agent is saying something wrong, and the column is not what decides whether it stops
   * — `sendOutboxRow` and the turn gate read the pack this process has LOADED. So the answer to
   * the button is kept and shown, in the sentence the API wrote for it.
   */
  const [moved, setMoved] = useState<MoveResult | null>(null);

  const refresh = async (): Promise<void> => { await qc.invalidateQueries({ queryKey: ['onboarding'] }); };

  const run = useMutation({
    mutationFn: async (step: StepId) => { setBusy(step); return client.runStep(step); },
    onSuccess: async () => { setBusy(null); setFailed(null); await refresh(); },
    onError: (e: Error) => { setBusy(null); setFailed(e.message); },
  });
  const confirm = useMutation({
    mutationFn: (v: { item: string; value: string }) => client.confirm(v.item, v.value),
    onSuccess: async () => { setFailed(null); await refresh(); },
    onError: (e: Error) => setFailed(e.message),
  });
  const move = useMutation({
    mutationFn: (v: { to: LadderStatus; reason: string }) => client.move(v.to, v.reason),
    onSuccess: async (r) => { setMoved(r); setReason(''); setFailed(null); await refresh(); },
    onError: (e: Error) => setFailed(e.message),
  });

  if (state.isError) {
    return <ErrorState error={state.error} context="open the onboarding checklist" onRetry={() => void state.refetch()} />;
  }
  const data = state.data;
  if (!data) return <p className="p-5 text-sm text-fg-muted">Reading where this tenant has got to…</p>;

  const confirmed = new Set(data.checklist.map((c) => c.item));

  return (
    <div className="h1-scroll h-full overflow-y-auto px-5 py-5">
      <div className="mx-auto flex max-w-5xl flex-col gap-5">
        <header className="flex flex-wrap items-center gap-3">
          <h1 className="text-base font-semibold text-fg">Onboarding</h1>
          <Pill tone={data.tenant.status === 'live' ? 'ok' : 'brand'}>{data.tenant.status}</Pill>
          <span className="text-xs text-fg-muted">
            {data.tenant.id} · answers in {data.tenant.languages.join(', ')} · pack {data.tenant.packDigest.slice(0, 12)}
          </span>
        </header>

        {data.packEditRequired && (
          <section role="alert" className="rounded-[var(--h1-radius)] border border-warn bg-warn-soft p-3 text-sm text-warn-soft-fg">
            <p className="font-semibold">
              The database says {data.tenant.status}; the pack still says {data.tenant.packStatus}.
            </p>
            <p className="mt-1">
              Change <code>{data.packEditRequired.field}</code> in <code>{data.packEditRequired.file}</code> from{' '}
              <code>{data.packEditRequired.from}</code> to <code>{data.packEditRequired.to}</code> and redeploy the pack.{' '}
              {data.packEditRequired.why}
            </p>
          </section>
        )}

        {failed && (
          <section role="alert" className="whitespace-pre-wrap rounded-[var(--h1-radius)] border border-danger bg-danger-soft p-3 text-sm text-danger-soft-fg">
            {failed}
          </section>
        )}

        {/* ---------------------------------------------------------------- the steps */}
        <section className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold text-fg">Steps</h2>
          {runGate && (
            <p role="status" className="rounded-[var(--h1-radius)] bg-neutral-soft px-3 py-2 text-xs text-neutral-soft-fg">
              {runGate} What every step found is on this page either way — that is what you would be
              asked about.
            </p>
          )}
          {data.steps.map((s) => (
            <StepCard
              key={s.step}
              step={s}
              busy={busy === s.step}
              onRun={() => run.mutate(s.step)}
              gate={runGate}
            />
          ))}
        </section>

        {/* ------------------------------------------------------------- the checklist */}
        <section className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold text-fg">Confirmed with the client</h2>
          <p className="text-xs text-fg-muted">
            The things no machine can check. The pack schema requires business hours, so every pack has
            some — what can be missing is anybody at the client having agreed to them.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-fg">Business hours: <code>{data.tenant.businessHours}</code></span>
            {confirmed.has('business_hours') ? (
              <Pill tone="ok">
                confirmed by {data.checklist.find((c) => c.item === 'business_hours')!.confirmedBy}
              </Pill>
            ) : (
              <Button
                size="sm"
                disabled={Boolean(runGate)}
                title={runGate}
                onClick={() => confirm.mutate({ item: 'business_hours', value: data.tenant.businessHours })}
              >
                I confirmed these with the client
              </Button>
            )}
          </div>
        </section>

        {/* ------------------------------------------------------------------ the gates */}
        <section className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold text-fg">Gates</h2>
          <ul className="flex flex-col gap-2">
            {data.gates.map((g) => <GateRow key={g.id} gate={g} />)}
          </ul>
        </section>

        {/* ------------------------------------------------------------------ the ladder */}
        <section className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold text-fg">Status</h2>
          {moveGate && (
            <p role="status" className="rounded-[var(--h1-radius)] bg-neutral-soft px-3 py-2 text-xs text-neutral-soft-fg">
              {moveGate} The gates below say what is still owed, so you can tell the owner what to
              look at.
            </p>
          )}
          <label className="flex flex-col gap-1 text-xs text-fg-muted">
            Why this tenant is moving — recorded on the transition
            <input
              className={clsx(INPUT, 'max-w-xl')}
              aria-label="Reason for the status change"
              disabled={!mayMove}
              title={moveGate}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="pack valid, credentials proved, suite green, reps set"
            />
          </label>
          <div className="flex flex-col gap-2">
            {data.moves.map((m) => (
              <div key={m.to} className="rounded-[var(--h1-radius)] border border-line p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant={m.to === 'paused' ? 'secondary' : 'primary'}
                    disabled={!mayMove || !m.allowed || reason.trim().length < 3}
                    title={moveGate}
                    onClick={() => move.mutate({ to: m.to as LadderStatus, reason })}
                  >
                    Move to {m.to}
                  </Button>
                  {!m.allowed && m.notAllowedWhy && <span className="text-xs text-fg-muted">{m.notAllowedWhy}</span>}
                  {!m.allowed && !m.notAllowedWhy && (
                    <span className="text-xs text-fg-muted">
                      {m.unmet.length} gate{m.unmet.length === 1 ? '' : 's'} not met
                    </span>
                  )}
                  {m.allowed && reason.trim().length < 3 && (
                    <span className="text-xs text-fg-muted">say why first</span>
                  )}
                </div>
                {m.unmet.length > 0 && (
                  <ul className="mt-2 flex flex-col gap-1">
                    {m.unmet.map((g) => (
                      <li key={g.id} className="text-xs text-fg-muted">
                        <span className="font-medium text-fg" title={g.id}>{g.title}</span> — {g.detail}
                        <br />
                        <span>what would satisfy it: {g.remedy}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
          {/*
            The API's own sentence, in full, under the buttons. `role="status"` rather than an
            alert either way: a move that did take is not a problem, and a move that did not is not
            a failure of the request — the row moved, it is audited, and what is owed is a restart.
            The honest-positive stays plainly different from the doubtful one, in the same quiet
            register the Health page uses for an "unknown" service: muted grey and no lead-in when
            the process obeys, "Not in force here." in full weight when it does not.
          */}
          {moved && (
            <p
              role="status"
              data-testid="status-in-force"
              className={clsx('text-xs', moved.inForce ? 'text-fg-muted' : 'text-fg')}
            >
              {moved.inForce ? '' : <span className="font-semibold">Not in force here. </span>}
              {moved.inForceDetail}
            </p>
          )}
        </section>

        {/*
          `formatDateTime`, not a sliced ISO string. `movedAt` and `ranAt` arrive as UTC, and
          `.slice(0, 16)` printed the UTC wall clock with nothing saying so — "09:15" for something
          that happened at 14:45 IST. On a page whose whole job is saying what was verified and
          when, a timestamp five and a half hours out is the wrong kind of wrong.
        */}
        {data.transitions.length > 0 && (
          <section className="flex flex-col gap-2">
            <h2 className="text-sm font-semibold text-fg">History</h2>
            <ul className="flex flex-col gap-1">
              {data.transitions.map((t) => (
                <li key={t.id} className="text-xs text-fg-muted">
                  {formatDateTime(t.movedAt)} · {t.from} → {t.to} · {t.movedBy} · {t.reason}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}

function StepCard({ step, busy, onRun, gate }: {
  step: Step; busy: boolean; onRun: () => void;
  /** Why this role may not run the step, or undefined when it may. */
  gate: string | undefined;
}): ReactNode {
  return (
    <article className="rounded-[var(--h1-radius)] border border-line p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-fg">{step.title}</h3>
        <Pill tone={stateTone(step.state)}>{stateLabel(step.state)}</Pill>
        {step.ranAt && <span className="text-xs text-fg-muted">{formatDateTime(step.ranAt)} · {step.ranBy}</span>}
        <span className="grow" />
        <Button size="sm" variant="secondary" disabled={busy || Boolean(gate)} title={gate} onClick={onRun}>
          {busy ? 'Running…' : step.state === 'not_run' ? 'Run' : 'Run again'}
        </Button>
      </div>
      <p className="mt-1 text-xs text-fg-muted">{step.verifies}</p>
      {step.state !== 'not_run' && <p className="mt-2 text-sm text-fg">{step.detail}</p>}
      {step.findings.length > 0 && (
        <ul className="mt-2 flex list-disc flex-col gap-1 pl-5">
          {step.findings.map((f) => <li key={f} className="text-xs text-fg-muted">{f}</li>)}
        </ul>
      )}
    </article>
  );
}

function GateRow({ gate }: { gate: Gate }): ReactNode {
  return (
    <li className="rounded-[var(--h1-radius)] border border-line p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={gate.satisfied ? 'ok' : gate.satisfiable ? 'warn' : 'danger'} dot>
          {gate.satisfied ? 'satisfied' : gate.satisfiable ? 'not yet' : 'not satisfiable'}
        </Pill>
        <span className="text-sm font-medium text-fg">{gate.title}</span>
        <code className="text-[11px] text-fg-subtle">{gate.id}</code>
      </div>
      <p className="mt-1 text-xs text-fg-muted">{gate.detail}</p>
      {!gate.satisfied && <p className="mt-1 text-xs text-fg-muted">what would satisfy it: {gate.remedy}</p>}
    </li>
  );
}
